import type { Ref } from 'react'
import { Link } from '@/components/Link'
import type { LinkField } from '@/types/api'
import { linkTargetProps } from '@/utils/linkTarget'

/** `card` is the boxed infobox; `inline` is a single compact row for headers. */
export type CommunityInfoVariant = 'card' | 'inline'

export interface CommunityInfoProps {
	name: string
	headingRef?: Ref<HTMLHeadingElement>
	address?: string
	websiteLink?: LinkField | null
	phone?: string
	phoneHref?: string
	/** @default "card" */
	variant?: CommunityInfoVariant
	spacesClass?: string
	className?: string
}

interface VariantClasses {
	root: string
	name: string
	address: string
	actions: string
	link: string
}

const variantClasses: Record<CommunityInfoVariant, VariantClasses> = {
	card: {
		root: 'spaces-community-info spaces:flex spaces:flex-col spaces:gap-4 spaces:p-16 spaces:rounded-16 spaces:bg-canvas-4',
		name: 'spaces:text-18/24',
		address: 'spaces:text-16/24',
		actions: 'spaces:flex-wrap',
		link: 'spaces:text-16/24'
	},
	inline: {
		root: 'spaces:flex spaces:flex-1 spaces:min-w-0 spaces:items-center spaces:justify-end spaces:gap-12',
		name: 'spaces:max-w-full spaces:text-14/24',
		address: 'spaces:min-w-0 spaces:text-14/24',
		actions: 'spaces:shrink-0 spaces:whitespace-nowrap',
		link: 'spaces:text-14/16'
	}
}

export function CommunityInfo ({
	name,
	headingRef,
	address,
	variant = 'card',
	spacesClass = '',
	className = '',
	...actions
}: CommunityInfoProps) {
	const styles = variantClasses[variant]
	const classes = [
		styles.root,
		spacesClass,
		className
	]
		.filter(Boolean)
		.join(' ')

	const heading = (
		<>
			<h2 ref={headingRef} tabIndex={-1} className={`spaces-community-info-name spaces:outline-none spaces:truncate ${styles.name} spaces:font-strong spaces:text-content-1`}>{name}</h2>
			{address && (
				<p className={`spaces-community-info-address spaces:truncate ${styles.address} spaces:font-default spaces:text-content-2`}>{address}</p>
			)}
		</>
	)

	return (
		<div className={classes}>
			{variant === 'inline'
				? <div className='spaces:flex spaces:min-w-0 spaces:items-center spaces:gap-8 spaces:whitespace-nowrap'>{heading}</div>
				: heading}
			<CommunityInfoActions {...actions} styles={styles} />
		</div>
	)
}

type CommunityInfoActionsProps = Pick<CommunityInfoProps, 'websiteLink' | 'phone' | 'phoneHref'> & {
	styles: VariantClasses
}

function CommunityInfoActions ({ websiteLink, phone, phoneHref, styles }: CommunityInfoActionsProps) {
	const showPhone = !!phone && !!phoneHref
	if (!websiteLink && !showPhone) return null

	return (
		<div className={`spaces-community-info-actions spaces:flex spaces:items-center spaces:gap-8 ${styles.actions}`}>
			{websiteLink && (
				<Link
					href={websiteLink.url}
					label={websiteLink.title}
					{...linkTargetProps(websiteLink.target)}
					defaultUnderline={false}
					spacesClass='spaces-community-info-website'
					className={styles.link}
				/>
			)}
			{websiteLink && showPhone && (
				<span aria-hidden='true' className='spaces:self-stretch spaces:border-l spaces:border-canvas-2' />
			)}
			{showPhone && (
				<Link
					href={phoneHref}
					label={phone}
					defaultUnderline={false}
					spacesClass='spaces-community-info-phone'
					className={styles.link}
				/>
			)}
		</div>
	)
}
